<aside class="w-64 bg-white dark:bg-[#151c2b] border-r border-slate-200 dark:border-slate-800 flex flex-col h-screen shrink-0 relative z-20">
  <!-- Logo -->
  <div class="h-14 flex items-center px-5 border-b border-slate-100 dark:border-slate-800/50">
    @if ((sidebarTitleType === 'imageOnly' || !projectName) && imgPath) {
      <img [src]="imgPath" alt="" class="h-full w-full object-contain py-2" />
    } @else {
      <div class="flex items-center gap-2.5">
        @if (imgPath) {
          <img [src]="imgPath" alt="" class="size-7 rounded object-contain" />
        } @else {
          <div class="size-7 rounded bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white shadow-sm shadow-primary-500/20">
            <span class="material-symbols-outlined text-[16px]">apps</span>
          </div>
        }
        <div>
          <h1 class="text-[13px] font-bold text-slate-900 dark:text-white leading-tight tracking-tight" [innerHTML]="projectName"></h1>
          @if (projectSubtitle) {
            <p class="text-[10px] text-slate-500 dark:text-slate-400 font-medium">{{ projectSubtitle }}</p>
          }
        </div>
      </div>
    }
  </div>

  <!-- Navigation -->
  <nav class="flex-1 overflow-y-auto py-3 px-3 flex flex-col gap-0.5">
    <div class="px-2 pb-1.5 pt-0.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{{ translationsService.translate('WebGalis_Sidebar_Navigation') }}</div>

    <!-- Dashboard
    <a (click)="navigateToDashboard()" [class]="itemClasses(isDashboardActive())">
      <span class="material-symbols-outlined" [class.text-primary-500]="isDashboardActive()" [class.icon-fill]="isDashboardActive()">dashboard</span>
      <span class="text-[13px] flex-1 text-left">Dashboard</span>
    </a> -->

    <!-- Top-level nodes -->
    @for (node of displayNodes(); track node.id) {
      @if (!node.isLeaf) {
        <!-- Section with children -->
        <button (click)="toggleSection(node.id)" [class]="sectionClasses(isSectionActive(node))">
          <span class="material-symbols-outlined" [class.text-primary-500]="isSectionActive(node)" [class.icon-fill]="isSectionActive(node)">{{ mapIcon(node.icon) }}</span>
          <span class="text-[13px] flex-1 text-left">{{ node.title }}</span>
          <span class="material-symbols-outlined transition-transform" [class.rotate-90]="!isExpanded(node.id)">
            expand_more
          </span>
        </button>

        @if (isExpanded(node.id)) {
          <div class="ml-[22px] border-l border-slate-200 dark:border-slate-700/50 flex flex-col">
            <ng-container *ngTemplateOutlet="nodeChildren; context: { $implicit: node.children }"></ng-container>
          </div>
        }
      } @else {
        <!-- Leaf node (no children) -->
        <a (click)="navigateToUrl(node.url)" [class]="itemClasses(isNodeUrlActive(node.url))">
          <span class="material-symbols-outlined" [class.text-primary-500]="isNodeUrlActive(node.url)" [class.icon-fill]="isNodeUrlActive(node.url)">{{ mapIcon(node.icon) }}</span>
          <span class="text-[13px] flex-1 text-left">{{ node.title }}</span>
        </a>
      }
    }

    @if (displayNodes().length === 0) {
      <div class="px-2 py-1.5 text-[11px] text-slate-400 italic">Loading navigation…</div>
    }
  </nav>

  <!-- Status Footer -->
  <div class="px-3 py-2.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#121824]">
    <div class="flex items-center justify-between mb-1">
      <span class="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase">System</span>
      <span class="flex h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
    </div>
    <div class="flex items-center gap-1.5">
      <span class="material-symbols-outlined text-slate-400 text-[13px]">dns</span>
      <span class="text-[11px] font-medium text-slate-600 dark:text-slate-400">Online</span>
      <span class="text-[10px] text-slate-400 ml-auto">24ms</span>
    </div>
  </div>
</aside>

<!-- Recursive template for children at any depth -->
<ng-template #nodeChildren let-children>
  @for (child of children; track child.id) {
    @if (!child.isLeaf) {
      <!-- Branch node: clickable label + expand toggle -->
      <div class="flex items-center">
        <a (click)="child.url ? navigateToUrl(child.url) : toggleNode(child.id)"
           class="flex-1 pl-3.5 py-[5px] text-[12.5px] font-medium transition-all cursor-pointer"
           [class.text-primary-500]="isSectionActive(child)"
           [class.dark:text-primary-400]="isSectionActive(child)"
           [class.font-semibold]="isSectionActive(child)"
           [class.text-slate-500]="!isSectionActive(child)"
           [class.dark:text-slate-400]="!isSectionActive(child)"
           [class.hover:text-slate-800]="!isSectionActive(child)">
          {{ child.title }}
        </a>
        <button (click)="toggleNode(child.id); $event.stopPropagation()"
                class="pr-2 py-[5px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
          <span class="material-symbols-outlined text-[13px] transition-transform"
                [class.rotate-90]="!isExpanded(child.id)">
            expand_more
          </span>
        </button>
      </div>
      @if (isExpanded(child.id)) {
        <div class="ml-3 border-l border-slate-100 dark:border-slate-700/30 flex flex-col">
          <ng-container *ngTemplateOutlet="nodeChildren; context: { $implicit: child.children }"></ng-container>
        </div>
      }
    } @else {
      <!-- Leaf node -->
      <a (click)="navigateToUrl(child.url)" [class]="childClasses(isNodeUrlActive(child.url), child.depth)">
        {{ child.title }}
      </a>
    }
  }
</ng-template>
